import tw from 'twin.macro'
import { useState } from 'react'
import { graphql } from 'gatsby'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { useClickAnyWhere, useEventListener } from 'usehooks-ts'
import DynamicAction from '@/components/ui/dynamic-action'
import { AnimatePresence, motion } from 'framer-motion'
import colors from '@/constants/colors'

export const fragment = graphql`
  fragment HeaderDropdownMenu on ContentfulHeaderDropdownMenu {
    __typename
    id
    title
    links {
      ...CtaLink
    }
  }
`

type HeaderDropdownMenuProps = Queries.HeaderDropdownMenuFragment & {
  backgroundColor?: string
}

export default function HeaderDropdownMenu({
  title,
  links,
  backgroundColor = colors.primaryBlue,
}: HeaderDropdownMenuProps) {
  // Hide the outline only when the user clicks on the button
  const [hideOutline, setHideOutline] = useState(false)
  useClickAnyWhere(() => setHideOutline(false))
  useEventListener('keydown', () => {
    setHideOutline(false)
  })

  return (
    <Popover tw="relative" as="nav">
      {({ open }) => (
        <>
          <PopoverButton
            type="button"
            tw="flex items-center gap-1 py-3 px-1 text-base xl:text-lg"
            css={[
              hideOutline && tw`outline-none`,
              backgroundColor === colors.primaryBlue && tw`hover:text-gray-300`,
              backgroundColor === colors.white && tw`hover:text-indigoBlue`,
              tw`dark:hover:text-tealBlue`,
            ]}
            onClick={() => setHideOutline(true)}
          >
            <span>{title}</span>
            <span tw="font-bwi text-xs duration-150 rotate-0" css={[open && tw`-rotate-90`]}>
              &#xe92d;
            </span>
          </PopoverButton>

          <AnimatePresence>
            {open && (
              <PopoverPanel
                tw="absolute z-10 py-4 bg-white rounded-2xl overflow-hidden min-w-[21rem] drop-shadow-xl"
                static
                as={motion.div}
                initial={{
                  transitionDuration: '50ms',
                  transitionProperty: 'opacity transform',
                  transitionTimingFunction: 'ease-out',
                  opacity: 0.9,
                  scale: 0.95,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                exit={{}}
              >
                <div tw="grid m-2">
                  {links.map((link) => (
                    <DynamicAction
                      key={link.id}
                      className="arrow-link"
                      tw="no-underline flex items-center gap-1 py-2 px-6 duration-200 text-primaryBlue! hover:bg-gray-100 hover:text-indigoBlue! rounded-md"
                      {...(link as any)}
                    />
                  ))}
                </div>
              </PopoverPanel>
            )}
          </AnimatePresence>
        </>
      )}
    </Popover>
  )
}
